import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type SlideContextValue = {
  currentSlide: number;
  totalSlides: number;
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
  isFirst: boolean;
  isLast: boolean;
  registerNextInterceptor: (fn: () => void) => void;
  unregisterNextInterceptor: () => void;
  registerPrevInterceptor: (fn: () => void) => void;
  unregisterPrevInterceptor: () => void;
};

const SlideContext = createContext<SlideContextValue | null>(null);

export function useSlideContext(): SlideContextValue {
  const ctx = useContext(SlideContext);
  if (!ctx) throw new Error("useSlideContext must be used within SlideProvider");
  return ctx;
}

type Props = {
  totalSlides: number;
  initialSlide?: number;
  children: ReactNode;
};

export function SlideProvider({ totalSlides, initialSlide = 0, children }: Props) {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const nextInterceptorRef = useRef<(() => void) | null>(null);
  const prevInterceptorRef = useRef<(() => void) | null>(null);

  const registerNextInterceptor = useCallback((fn: () => void) => {
    nextInterceptorRef.current = fn;
  }, []);

  const unregisterNextInterceptor = useCallback(() => {
    nextInterceptorRef.current = null;
  }, []);

  const registerPrevInterceptor = useCallback((fn: () => void) => {
    prevInterceptorRef.current = fn;
  }, []);

  const unregisterPrevInterceptor = useCallback(() => {
    prevInterceptorRef.current = null;
  }, []);

  const next = useCallback(() => {
    if (nextInterceptorRef.current) {
      nextInterceptorRef.current();
      return;
    }
    setCurrentSlide((s) => Math.min(s + 1, totalSlides - 1));
  }, [totalSlides]);

  const prev = useCallback(() => {
    if (prevInterceptorRef.current) {
      prevInterceptorRef.current();
      return;
    }
    setCurrentSlide((s) => Math.max(s - 1, 0));
  }, []);

  const goTo = useCallback(
    (index: number) => {
      setCurrentSlide(Math.max(0, Math.min(index, totalSlides - 1)));
    },
    [totalSlides],
  );

  const value = useMemo(
    () => ({
      currentSlide,
      totalSlides,
      next,
      prev,
      goTo,
      isFirst: currentSlide === 0,
      isLast: currentSlide === totalSlides - 1,
      registerNextInterceptor,
      unregisterNextInterceptor,
      registerPrevInterceptor,
      unregisterPrevInterceptor,
    }),
    [
      currentSlide,
      totalSlides,
      next,
      prev,
      goTo,
      registerNextInterceptor,
      unregisterNextInterceptor,
      registerPrevInterceptor,
      unregisterPrevInterceptor,
    ],
  );

  return <SlideContext.Provider value={value}>{children}</SlideContext.Provider>;
}
